import { action, computed, makeAutoObservable, observable, reaction } from 'mobx'

import { IRootStore } from 'store/types'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'

import { TicketStatus, TicketType } from '../../types'
import { Ticket } from '../entity/Ticket'
import { ViewTicketApiStore } from './ViewTicketApiStore'

export class ViewTicketUiStore {
  @observable page = 1
  @observable pageSize = 20
  @observable search = ''
  @observable searchTrigger = false
  @observable status: TicketStatus | null = null
  @observable ticketType: TicketType | null = null

  @observable selectedTicket: Ticket | null = null
  @observable message = ''
  @observable files: ConvertedFile[] = []

  @observable private readonly apiStore = new ViewTicketApiStore(this)

  constructor(readonly rootStore: IRootStore) {
    makeAutoObservable(this)

    reaction(
      () => [this.searchTrigger, this.status, this.ticketType],
      () => {
        void this.getTicketList()
      },
    )
  }

  @computed
  get ticketStore() {
    return this.rootStore.ticketStore
  }

  @computed
  get ticketList() {
    return this.ticketStore.list
  }

  @computed
  getTicket(ticketId: number) {
    return this.ticketStore.getEntity(ticketId) ?? null
  }

  @computed
  get emptyFilters() {
    return this.search === '' && this.status === null && this.ticketType === null
  }

  @action
  setPage(value: number) {
    this.page = value
  }

  @action
  setPageSize(value: number) {
    this.pageSize = value
  }

  @action
  setSearch(value: string) {
    if (this.search !== value) {
      this.search = value.trim()
      this.setPage(1)
    }
  }

  @action
  setSearchTrigger(value: boolean) {
    this.searchTrigger = value
  }

  @action
  setStatus(value: TicketStatus | null) {
    this.status = value
    this.setPage(1)
  }

  @action
  setTicketType(value: TicketType | null) {
    this.ticketType = value
    this.setPage(1)
  }

  /** @param isSameLink Если true, то будет сброшен selectedTicket
   * Нужен для перерисовки, когда ссылка на элемент не меняется, но меняются отдельные поля
   */
  @action
  setSelectedTicket = (value: Ticket | null, isSameLink: boolean = false) => {
    if (isSameLink) {
      this.selectedTicket = null
    }

    this.selectedTicket = value
  }

  @action
  setMessage = (value: string) => {
    this.message = value
  }

  @action
  setFiles(value: ConvertedFile[]) {
    this.files = value
  }

  reset = () => {
    this.setMessage('')
    this.setFiles([])
  }

  async getTicketList(disableLoading: boolean = false) {
    return await this.ticketStore.loadList(disableLoading, {
      page: this.page,
      pageSize: this.pageSize,
      search: this.search,
      ticketStateId: this.status ?? undefined,
      sortByCreateDate: 'desc',
      ticketTypeIds: this.ticketType === null ? [TicketType.openingOrder, TicketType.roomLayout] : [this.ticketType],
    })
  }

  async createSupportLog() {
    return await this.apiStore.createSupportLog()
  }

  async closeTicket() {
    return await this.apiStore.closeTicket()
  }

  async rateTicket(grade: number, message: string) {
    return await this.apiStore.rateTicket(grade, message)
  }
}
